import { useState, type FormEvent } from 'react';
import { adminSignupGateway } from '../../services/AdminSignupGateway';
import { feinDigitsFromInput, formatFeinDisplay, formatUsPhoneDisplay, normalizeWebsiteUrl, usPhoneDigitsFromInput } from '../../../../shared/formatting';
import type { StepProps } from '../AdminSignUpPage';
import { friendlyErrorMessage } from '../../../../shared/friendlyError';

/** Step 1 — Company Information. Submitting this step is what calls the anonymous
 * `POST /api/registrations` and receives the onboarding-only cookie every later step depends on (see
 * OnboardingAuthCookies on the backend) — no Owner account or password is collected here (Decision 1's
 * correction: that happens at Step 4). There is deliberately no "Skip for now" on this step, matching the
 * FINAL approved HTML (CDR: "core company information is required"). Field grouping, the substepper, and
 * the employee counter all mirror the approved HTML's `#companyInfoScreen` markup. */
export function CompanyInfoStep({ goNext }: StepProps) {
  const [companyName, setCompanyName] = useState('');
  const [employeeCount, setEmployeeCount] = useState(0);
  const [ein, setEin] = useState('');
  const [sein, setSein] = useState('');
  const [website, setWebsite] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await adminSignupGateway.createRegistration({
        companyName,
        ein: ein || null,
        sein: sein || null,
        website: normalizeWebsiteUrl(website) || null,
        contactEmail: contactEmail || null,
        contactPhone: contactPhone || null,
        employeeCount,
      });
      goNext();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not save company information.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="screen active" data-testid="company-info-step">
      <div className="screen-icon" aria-hidden="true">
        🏢
      </div>
      <h2>Step 1: Company Information</h2>
      <div className="substepper" aria-hidden="true">
        <div className="substep active">
          <span className="dot">1</span>Company Info
        </div>
        <span className="subline" />
        <div className="substep">
          <span className="dot">2</span>Identity Verification
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="field-grid">
          <div className="field">
            <label htmlFor="companyName">
              Company Name <span className="req">*</span>
            </label>
            <input className="control" id="companyName" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required autoFocus />
          </div>

          <div className="field">
            <label htmlFor="employeeCount">
              No. of Employees <span className="req">*</span>
            </label>
            <div className="counter">
              <button type="button" aria-label="Decrease number of employees" onClick={() => setEmployeeCount((n) => Math.max(0, n - 1))}>
                −
              </button>
              <input
                id="employeeCount"
                type="number"
                min={0}
                value={employeeCount}
                onChange={(e) => setEmployeeCount(Number(e.target.value))}
                required
              />
              <button type="button" aria-label="Increase number of employees" onClick={() => setEmployeeCount((n) => n + 1)}>
                +
              </button>
            </div>
          </div>

          <div className="field">
            <label htmlFor="ein">
              FEIN <span className="req">*</span>
            </label>
            <input
              className="control"
              id="ein"
              inputMode="numeric"
              autoComplete="off"
              value={formatFeinDisplay(ein)}
              onChange={(e) => setEin(feinDigitsFromInput(e.target.value))}
              required
              placeholder="XX-XXXXXXX"
              maxLength={10}
            />
          </div>

          <div className="field">
            <label htmlFor="sein">SEIN</label>
            <input className="control" id="sein" value={sein} onChange={(e) => setSein(e.target.value)} placeholder="State-issued format" maxLength={24} />
          </div>

          <div className="field full">
            <label htmlFor="website">Company Website</label>
            <input
              className="control"
              id="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              onBlur={(e) => setWebsite(normalizeWebsiteUrl(e.target.value))}
              placeholder="https://www.company.com"
            />
          </div>

          <div className="field full">
            <label htmlFor="contactEmail">
              Company Email <span className="req">*</span>
            </label>
            <input
              className="control"
              id="contactEmail"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="name@company.com"
              autoComplete="email"
              required
            />
          </div>

          <div className="field full">
            <label htmlFor="contactPhone">
              Company Phone Number <span className="req">*</span>
            </label>
            <input
              className="control"
              id="contactPhone"
              inputMode="tel"
              value={formatUsPhoneDisplay(contactPhone)}
              onChange={(e) => setContactPhone(usPhoneDigitsFromInput(e.target.value))}
              placeholder="(XXX) XXX-XXXX"
              autoComplete="tel"
              maxLength={14}
              required
            />
          </div>
        </div>

        {error && (
          <p className="error-line" role="alert">
            <span className="info-icon">i</span>
            <span>{error}</span>
          </p>
        )}

        <div className="actions">
          <button className="btn btn-primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save and Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
